import { studentAPI } from '../services/api';
import { isProfilePhotoDocumentType, effectiveFileRule } from './documentType';

interface ProfilePhotoDocumentType {
  id: string;
  code?: string | null;
  name?: string | null;
  allowedFileTypes?: string | null;
  allowedFileSize?: number | null;
}

type ProfilePhotoUploadResult = { ok: true } | { ok: false; message: string };


interface PickedPhoto {
  name: string;
  size?: number;
  value: any;
}


let docTypePromise: Promise<ProfilePhotoDocumentType | null> | null = null;


function resolveProfilePhotoDocumentType(): Promise<ProfilePhotoDocumentType | null> {
  if (!docTypePromise) {
    docTypePromise = studentAPI
      .getDocumentTypes('ENROLLED')
      // Same response shape the Documents tiles read: a bare array with the
      // server's PascalCase field names.
      .then(({ data }) => ((Array.isArray(data) ? data : []) as any[])
        .map((d): ProfilePhotoDocumentType => ({
          id: d.Id || d.id,
          name: d.Name || d.name,
          code: d.Code || d.code,
          allowedFileTypes: d.AllowedFileTypes || d.allowedFileTypes || '',
          allowedFileSize: d.AllowedFileSize || d.allowedFileSize || 0,
        }))
        .find(isProfilePhotoDocumentType) || null)
      .catch(() => {
        // Don't cache a network failure — the next upload should try again.
        docTypePromise = null;
        return null;
      });
  }
  return docTypePromise;
}


export async function writeProfilePhoto(
  studentProfileId: string,
  picked: PickedPhoto,
): Promise<ProfilePhotoUploadResult> {
  try {
    const form = new FormData();
    form.append('file', picked.value);
    await studentAPI.uploadStudentPhoto(studentProfileId, form);
    return { ok: true };
  } catch (err: any) {
    return { ok: false, message: photoErrorMessage(err) };
  }
}


export async function uploadProfilePhoto(
  studentProfileId: string,
  picked: PickedPhoto,
): Promise<ProfilePhotoUploadResult> {
  const docType = await resolveProfilePhotoDocumentType();

  if (!docType?.id) {
    return writeProfilePhoto(studentProfileId, picked);
  }

  const rule = effectiveFileRule(docType);

  const ext = picked.name.split('.').pop()?.toLowerCase() || '';
  if (rule.extensions.length > 0 && !rule.extensions.includes(ext)) {
    return { ok: false, message: `Only ${rule.extensions.join(', ').toUpperCase()} images are allowed.` };
  }
  if (rule.maxMb > 0 && (picked.size || 0) > rule.maxMb * 1024 * 1024) {
    return { ok: false, message: `Image must be under ${rule.maxMb} MB.` };
  }

  try {
    const form = new FormData();
    form.append('File', picked.value);
    await studentAPI.uploadStudentDocumentByProfile(docType.id, studentProfileId, form);
  } catch (err: any) {
    return { ok: false, message: photoErrorMessage(err) };
  }

  return writeProfilePhoto(studentProfileId, picked);
}

/** Both endpoints report failures under differently-cased keys. */
function photoErrorMessage(err: any): string {
  const body = err?.response?.data;
  return (
    body?.Error || body?.error || body?.Message || body?.message ||
    'Failed to update photo. Please try again.'
  );
}
